/* Custom media reviews — carousel + auto-rotate + click-to-play video.

   Loop mechanics ported verbatim from custom-testimonials.js (the tried
   pattern): clone perViewDesktop cards from each end, scroll-snap rail,
   watch for scroll into clone region after a brief settle, then teleport
   silently to the equivalent real-card position. Mouse-drag swipe on
   desktop. Auto-rotate + dots + play buttons are media-reviews-specific
   additions on top. */
(function () {
  if (customElements.get('custom-media-reviews')) return;

  const SCROLL_SETTLE_MS = 80;

  class CustomMediaReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;

      this.originals = Array.from(this.track.querySelectorAll('[data-card]'));
      this.realCount = this.originals.length;
      this.rotationMode = this.dataset.rotationMode === 'marquee' ? 'marquee' : 'snap';

      if (this.rotationMode === 'marquee' && this.realCount > 0) {
        this._setupMarquee();
        this._setupPlayButtons();
        return;
      }

      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');

      // Use desktop per-view for cloning so we have enough buffer even on
      // mobile per-view = 1. Same trick testimonials uses.
      this.perViewDesktop = this._readPerViewDesktop();
      this.cloneCount = Math.min(this.perViewDesktop, this.realCount);

      const loopEnabled = this.realCount > 1;
      if (loopEnabled) {
        this._addClones();
        requestAnimationFrame(() => {
          this._setInitialPosition();
          this._setupScrollLoopWatcher();
        });
      }

      this.cards = Array.from(this.track.querySelectorAll('[data-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));

      this.prevBtn?.addEventListener('click', () => this._scrollByCards(-1));
      this.nextBtn?.addEventListener('click', () => this._scrollByCards(1));

      this._setupDrag();
      this._setupDots();
      this._setupScrollSync();
      this._setupPlayButtons();
      this._setupAutoRotate();

      window.addEventListener('resize', () => this._handleResize(), { passive: true });
    }

    /* === Marquee mode (continuous scroll, benefits-bar style) ===
       Track becomes a flex row of fixed-width cards. We duplicate the
       originals once, then drive translateX with rAF so the loop is
       pixel-perfect regardless of card widths. */

    _setupMarquee() {
      // Duplicate the originals once — two identical copies means jumping
      // by -oneCopyWidth lands the second copy exactly where the first
      // started, so there's no seam.
      this.originals.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.setAttribute('data-clone', 'marquee');
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      });

      this._marqueeSpeed = parseFloat(this.dataset.marqueeSpeed) || 40; // px/s
      this._marqueeDir = this.dataset.marqueeDirection === 'right' ? 1 : -1;
      this._marqueePaused = false;
      this._marqueeX = 0;
      this._marqueeOneCopy = 0;

      this._layoutMarqueeCards();
      requestAnimationFrame(() => this._runMarquee());

      const pauseOnHover = this.dataset.pauseOnHover === 'true';
      if (pauseOnHover) {
        this.addEventListener('mouseenter', () => (this._marqueePaused = true));
        this.addEventListener('mouseleave', () => (this._marqueePaused = false));
        this.addEventListener('touchstart', () => (this._marqueePaused = true), { passive: true });
        this.addEventListener(
          'touchend',
          () => setTimeout(() => (this._marqueePaused = false), 1500),
          { passive: true }
        );
      }

      let rTimer;
      window.addEventListener(
        'resize',
        () => {
          clearTimeout(rTimer);
          rTimer = setTimeout(() => this._layoutMarqueeCards(), 120);
        },
        { passive: true }
      );
    }

    _layoutMarqueeCards() {
      // Match the snap-mode per-view sizing: card width fits perView items
      // across the section container, with the same gap.
      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const cs = getComputedStyle(this);
      const perView = parseInt(
        cs.getPropertyValue(isDesktop ? '--mr-per-desktop' : '--mr-per-mobile'),
        10
      ) || (isDesktop ? 4 : 2);
      const gap = parseInt(
        cs.getPropertyValue(isDesktop ? '--mr-card-gap-desktop' : '--mr-card-gap-mobile'),
        10
      ) || 8;

      // Container width = the carousel wrapper (constrained by mr-max-width + section padding).
      const carousel = this.querySelector('.custom-mr__carousel');
      const containerWidth = (carousel || this).getBoundingClientRect().width;
      const cardWidth = Math.max(80, (containerWidth - gap * (perView - 1)) / perView);

      const all = Array.from(this.track.children);
      all.forEach((card) => {
        card.style.width = cardWidth + 'px';
      });

      // One copy = N cards + N gaps (the trailing gap leads into the duplicate).
      this._marqueeOneCopy = cardWidth * this.realCount + gap * this.realCount;
      // Keep current offset inside the valid range after resize.
      if (Math.abs(this._marqueeX) >= this._marqueeOneCopy) {
        this._marqueeX = this._marqueeX % this._marqueeOneCopy;
      }
      this.track.style.transform = `translate3d(${this._marqueeX}px, 0, 0)`;
    }

    _runMarquee() {
      let last = performance.now();
      const step = (now) => {
        const dt = Math.min(64, now - last) / 1000; // clamp large tab-switch gaps
        last = now;
        if (!this._marqueePaused && !this.videoActive && this._marqueeOneCopy > 0) {
          this._marqueeX += this._marqueeDir * this._marqueeSpeed * dt;
          // Normalize so the offset always sits within (-oneCopy, 0] for
          // dir -1 or [0, oneCopy) for dir +1.
          if (this._marqueeDir < 0 && this._marqueeX <= -this._marqueeOneCopy) {
            this._marqueeX += this._marqueeOneCopy;
          } else if (this._marqueeDir > 0 && this._marqueeX >= this._marqueeOneCopy) {
            this._marqueeX -= this._marqueeOneCopy;
          }
          this.track.style.transform = `translate3d(${this._marqueeX}px, 0, 0)`;
        }
        this._marqueeRaf = requestAnimationFrame(step);
      };
      this._marqueeRaf = requestAnimationFrame(step);
    }

    disconnectedCallback() {
      if (this._marqueeRaf) cancelAnimationFrame(this._marqueeRaf);
    }

    _readPerViewDesktop() {
      const v = parseInt(getComputedStyle(this).getPropertyValue('--mr-per-desktop'), 10);
      return v > 0 ? v : 4;
    }

    /* === Clone + loop (testimonials pattern) === */

    _addClones() {
      const headClones = this.originals.slice(0, this.cloneCount).map((c) => {
        const clone = c.cloneNode(true);
        clone.setAttribute('data-clone', 'head');
        clone.setAttribute('aria-hidden', 'true');
        return clone;
      });
      const tailClones = this.originals.slice(-this.cloneCount).map((c) => {
        const clone = c.cloneNode(true);
        clone.setAttribute('data-clone', 'tail');
        clone.setAttribute('aria-hidden', 'true');
        return clone;
      });

      // Tail clones go at the start (in original order so the visual sequence
      // reading right-to-left is N-2, N-1, N before the first real card).
      // Head clones go at the end.
      tailClones.forEach((c) => this.track.appendChild(c)); // temp append
      tailClones.forEach((c) => this.track.insertBefore(c, this.track.firstChild));
      headClones.forEach((c) => this.track.appendChild(c));
    }

    _firstRealCard() {
      return this.track.children[this.cloneCount] || null;
    }
    _lastRealCard() {
      return this.track.children[this.cloneCount + this.realCount - 1] || null;
    }
    _firstHeadClone() {
      return this.track.children[this.cloneCount + this.realCount] || null;
    }
    _firstTailCloneWidth() {
      const last = this.track.children[this.cloneCount - 1];
      if (!last) return 0;
      return last.offsetLeft - this.track.offsetLeft + last.offsetWidth;
    }

    _setInitialPosition() {
      const firstReal = this._firstRealCard();
      if (!firstReal) return;
      this._jumpTo(firstReal.offsetLeft - this.track.offsetLeft);
    }

    _jumpTo(left) {
      const prev = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = left;
      // Force reflow to commit the position before restoring.
      // eslint-disable-next-line no-unused-expressions
      this.track.scrollLeft;
      this.track.style.scrollBehavior = prev || '';
    }

    _setupScrollLoopWatcher() {
      let scrollTimer;
      const schedule = (delay) => {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(() => this._maybeWrap(), delay);
      };
      this.track.addEventListener('scroll', () => schedule(SCROLL_SETTLE_MS), { passive: true });
      // Touchend backup — see testimonials for the iOS rationale.
      this.track.addEventListener('touchend', () => schedule(180), { passive: true });
    }

    _maybeWrap() {
      const firstReal = this._firstRealCard();
      const firstHeadClone = this._firstHeadClone();
      if (!firstReal || !firstHeadClone) return;

      const realStart = firstReal.offsetLeft - this.track.offsetLeft;
      const headStart = firstHeadClone.offsetLeft - this.track.offsetLeft;
      const scroll = this.track.scrollLeft;

      if (scroll >= headStart - 1) {
        // Past the real end into the head clones — teleport back to the
        // equivalent real-card position so the next swipe forward lands
        // on the second card, not the first again.
        const offset = scroll - headStart;
        this._jumpTo(realStart + offset);
      } else if (scroll <= realStart - this._firstTailCloneWidth() + 1) {
        // Before the real start into the tail clones — teleport forward
        // to the equivalent real-card position on the other end.
        const lastReal = this._lastRealCard();
        const realEnd = lastReal.offsetLeft - this.track.offsetLeft + lastReal.offsetWidth;
        const distance = realStart - scroll;
        this._jumpTo(realEnd - distance);
      }
    }

    /* === Arrow click — scroll by 1 card (media-reviews specific). === */

    _scrollByCards(direction) {
      const card = this._firstRealCard() || this.cards[0];
      if (!card) return;
      const styles = getComputedStyle(this.track);
      const gap = parseInt(styles.columnGap || styles.gap, 10) || 0;
      const cardWidth = card.getBoundingClientRect().width;
      this.track.scrollBy({ left: (cardWidth + gap) * direction, behavior: 'smooth' });
    }

    /* === Dots, scroll-sync (active state) === */

    _setupDots() {
      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const card = this.track.children[this.cloneCount + index];
          if (!card) return;
          this.track.scrollTo({
            left: card.offsetLeft - this.track.offsetLeft,
            behavior: 'smooth',
          });
        });
      });
    }

    _setupScrollSync() {
      let ticking = false;
      const update = () => {
        ticking = false;
        const active = this._activeRealIndex();
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === active);
        });
      };
      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      this.track.addEventListener('scroll', onScroll, { passive: true });
      requestAnimationFrame(update);
    }

    _activeRealIndex() {
      if (!this.cards.length) return -1;
      const center = this.track.scrollLeft + this.track.clientWidth / 2;
      let result = 0;
      let minDist = Infinity;
      this.cards.forEach((card) => {
        const mid = card.offsetLeft - this.track.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(mid - center);
        if (dist < minDist) {
          minDist = dist;
          if (card.dataset.clone === 'head') {
            // Head clones map back to real0, real1, ...
            const headIdx = Array.from(this.track.children).indexOf(card) - (this.cloneCount + this.realCount);
            result = Math.max(0, Math.min(this.realCount - 1, headIdx));
          } else if (card.dataset.clone === 'tail') {
            // Tail clones map to last cloneCount real cards.
            const tailIdx = Array.from(this.track.children).indexOf(card);
            result = this.realCount - this.cloneCount + tailIdx;
          } else {
            result = this.originals.indexOf(card);
          }
        }
      });
      return result;
    }

    /* === Mouse-drag swipe (desktop) === */

    _setupDrag() {
      let isDown = false;
      let startX = 0;
      let scrollLeft = 0;
      let moved = false;

      const onDown = (e) => {
        if (e.type === 'mousedown' && e.button !== 0) return;
        isDown = true;
        moved = false;
        startX = e.pageX - this.track.getBoundingClientRect().left;
        scrollLeft = this.track.scrollLeft;
        this.track.classList.add('is-dragging');
      };
      const onMove = (e) => {
        if (!isDown) return;
        const x = e.pageX - this.track.getBoundingClientRect().left;
        const walk = x - startX;
        if (Math.abs(walk) > 4) moved = true;
        this.track.scrollLeft = scrollLeft - walk;
      };
      const onUp = () => {
        if (!isDown) return;
        isDown = false;
        this.track.classList.remove('is-dragging');
      };

      this.track.addEventListener(
        'click',
        (e) => {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          }
        },
        true
      );
      this.track.addEventListener('mousedown', onDown);
      this.track.addEventListener('mousemove', onMove);
      this.track.addEventListener('mouseup', onUp);
      this.track.addEventListener('mouseleave', onUp);
    }

    _handleResize() {
      const firstReal = this._firstRealCard();
      if (!firstReal) return;
      const realStart = firstReal.offsetLeft - this.track.offsetLeft;
      if (this.track.scrollLeft < realStart) this._jumpTo(realStart);
    }

    /* === Play buttons (HTML5 video + iframe facade) === */

    _setupPlayButtons() {
      this.querySelectorAll('[data-play-btn]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const card = btn.closest('[data-card]');
          if (!card) return;

          const video = card.querySelector('[data-video]');
          if (video) {
            video.setAttribute('controls', '');
            card.classList.add('is-playing');
            this.videoActive = true;
            this._pauseAutoRotate();
            const p = video.play();
            if (p && p.catch) p.catch(() => {});
            const resume = () => {
              this.videoActive = false;
              this._resumeAutoRotate();
            };
            video.addEventListener('pause', resume, { once: true });
            video.addEventListener('ended', resume, { once: true });
            return;
          }

          const iframeHost = card.querySelector('[data-iframe-host]');
          if (iframeHost) {
            const type = iframeHost.dataset.videoType;
            const id = iframeHost.dataset.videoId;
            if (!type || !id) return;
            const iframe = document.createElement('iframe');
            iframe.setAttribute(
              'src',
              type === 'youtube'
                ? `https://www.youtube.com/embed/${id}?autoplay=1&playsinline=1&rel=0`
                : `https://player.vimeo.com/video/${id}?autoplay=1&playsinline=1`
            );
            iframe.setAttribute('frameborder', '0');
            iframe.setAttribute(
              'allow',
              'autoplay; encrypted-media; fullscreen; picture-in-picture'
            );
            iframe.setAttribute('allowfullscreen', '');
            iframe.setAttribute('title', 'Video');
            iframe.className = 'custom-mr__media custom-mr__media--iframe';
            Object.assign(iframe.style, {
              position: 'absolute',
              inset: '0',
              width: '100%',
              height: '100%',
              border: '0',
            });
            iframeHost.appendChild(iframe);
            card.classList.add('is-playing');
            this.videoActive = true;
            this._pauseAutoRotate();
          }
        });
      });
    }

    /* === Auto-rotate === */

    _setupAutoRotate() {
      const enabled = this.dataset.autorotate === 'true';
      const interval = parseInt(this.dataset.interval, 10) || 6000;
      const pauseOnHover = this.dataset.pauseOnHover === 'true';

      if (!enabled || this.realCount <= 1) return;

      this.autoInterval = interval;
      this.autoPaused = false;
      this.autoTimer = null;

      const tick = () => {
        if (this.autoPaused || this.videoActive) return;
        this._scrollByCards(1);
      };

      this._startAuto = () => {
        this._stopAuto();
        this.autoTimer = setInterval(tick, this.autoInterval);
      };
      this._stopAuto = () => {
        if (this.autoTimer) {
          clearInterval(this.autoTimer);
          this.autoTimer = null;
        }
      };
      this._pauseAutoRotate = () => {
        this.autoPaused = true;
      };
      this._resumeAutoRotate = () => {
        this.autoPaused = false;
      };

      this._startAuto();

      if (pauseOnHover) {
        this.addEventListener('mouseenter', () => this._pauseAutoRotate());
        this.addEventListener('mouseleave', () => this._resumeAutoRotate());
        this.addEventListener('touchstart', () => this._pauseAutoRotate(), { passive: true });
        this.addEventListener(
          'touchend',
          () => {
            setTimeout(() => this._resumeAutoRotate(), 2500);
          },
          { passive: true }
        );
      }
    }

    _pauseAutoRotate() {}
    _resumeAutoRotate() {}
  }

  customElements.define('custom-media-reviews', CustomMediaReviews);
})();
